import { useDashboardStore } from '@/state/store';

/**
 * Compact atmosphere readout shown in the top header.  Reflects the
 * latest ``building.weather_changed`` and ``building.sun_position``
 * events.  Renders nothing until the first event lands so we don't show
 * placeholder garbage on first load.
 *
 * The Pixi exterior scene already consumes the same state through the
 * snapshot builder; this DOM panel is the operator-facing summary
 * (useful when the Pixi renderer is hidden, e.g., on the DOM mock).
 */

const WEATHER_GLYPH: Record<string, string> = {
  sunny: '☀︎',
  cloudy: '⛅︎',
  storm: '⛈︎',
  windy: '🜨',
  snow: '❄︎',
  fog: '☁︎',
};

const PHASE_GLYPH: Record<string, string> = {
  predawn: '🜨',
  morning: '🌅',
  noon: '☀︎',
  afternoon: '🌇',
  evening: '🌆',
  night: '🌙',
  weekend_moon: '🌙',
  holiday_moon: '🌙',
};

const REGIME_COLOR: Record<string, string> = {
  bull: '#2ea043',
  bear: '#f85149',
  flat: '#8b949e',
  crisis: '#db6d28',
  recovery: '#a371f7',
};

function pillStyle(accent: string): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '3px 8px',
    background: '#161b22',
    border: `1px solid ${accent}`,
    borderRadius: 999,
    fontSize: 11,
    color: '#c9d1d9',
    fontVariantNumeric: 'tabular-nums',
  };
}

export function AtmosphereHud() {
  const atmosphere = useDashboardStore((s) => s.atmosphere);
  const { weather, sunPhase, marketRegime, regimeConfidence } = atmosphere;

  if (!weather && !sunPhase) return null;

  const regimeAccent = marketRegime
    ? REGIME_COLOR[marketRegime] ?? '#30363d'
    : '#30363d';

  return (
    <div
      aria-label="Atmosphere"
      style={{
        display: 'inline-flex',
        gap: 6,
        alignItems: 'center',
      }}
    >
      {weather ? (
        <span
          style={pillStyle('#30363d')}
          title={`Погода в «здании» (атмосфера дашборда), ${weather}. Маппится из building.weather_changed events: sunny — стабильный рынок, storm — высокий VIX/волатильность, fog — низкая ликвидность, и т.д. Чисто визуальный сигнал — не торговая метрика.`}
        >
          <span aria-hidden style={{ fontSize: 13 }}>
            {WEATHER_GLYPH[weather] ?? '·'}
          </span>
          <span style={{ textTransform: 'capitalize' }}>{weather}</span>
        </span>
      ) : null}
      {sunPhase ? (
        <span
          style={pillStyle('#30363d')}
          title={`Фаза дня (${sunPhase}), вычисляется из позиции солнца относительно торговой сессии MOEX. predawn — до открытия, morning/noon/afternoon — в сессии, evening — после закрытия, night/weekend — биржа закрыта.`}
        >
          <span aria-hidden style={{ fontSize: 13 }}>
            {PHASE_GLYPH[sunPhase] ?? '·'}
          </span>
          <span style={{ textTransform: 'capitalize' }}>
            {sunPhase.replace('_', ' ')}
          </span>
        </span>
      ) : null}
      {marketRegime ? (
        <span
          style={pillStyle(regimeAccent)}
          title={
            `Режим рынка по классификатору backend'а: ${marketRegime.toUpperCase()}. ` +
            (regimeConfidence != null
              ? `Уверенность модели: ${(regimeConfidence * 100).toFixed(0)}%. `
              : '') +
            'BULL — бычий тренд, BEAR — медвежий, FLAT — боковик, CRISIS — резкая просадка, RECOVERY — отскок после crisis. Влияет на target_gross PM\'а: в crisis уменьшается, в bull увеличивается.'
          }
        >
          <span style={{ color: regimeAccent, fontWeight: 700 }}>●</span>
          <span style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
            {marketRegime}
          </span>
          {regimeConfidence != null ? (
            <span style={{ color: '#6e7681' }}>
              · {(regimeConfidence * 100).toFixed(0)}%
            </span>
          ) : null}
        </span>
      ) : null}
    </div>
  );
}
